import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { registerUser } from "../utils/fetchFunctions";
import { isPasswordValid } from "../utils/passwordUtils";
import Button from "../components/Button";
import InputField from "../components/InputField";
import FormError from "../components/FormError";
import FormContainer from "../components/FormContainer";
import PasswordRules from "../components/PasswordRules";
import SelectField from "../components/SelectField";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import "./auth.css";

const validateForm = (formData) => {
  const {
    email,
    confirmEmail,
    password,
    confirmPassword,
    companyName,
    contactName,
    phoneNumber,
    salutation,
  } = formData;

  if (
    !email ||
    !confirmEmail ||
    !password ||
    !confirmPassword ||
    !companyName ||
    !contactName ||
    !phoneNumber ||
    !salutation
  ) {
    return "Please fill in all required fields.";
  }

  if (email !== confirmEmail) {
    return "The email addresses do not match.";
  }

  if (password !== confirmPassword) {
    return "The passwords do not match.";
  }

  if (!isPasswordValid(password)) {
    return "The password does not meet all requirements.";
  }

  return null;
};

const Register = () => {
  const [formData, setFormData] = useState({
    email: "",
    confirmEmail: "",
    password: "",
    confirmPassword: "",
    companyName: "",
    contactName: "",
    salutation: "",
    phoneNumber: "",
    website: "",
  });

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [emailSent, setEmailSent] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const validationError = validateForm(formData);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const response = await registerUser(
        formData.email,
        formData.confirmEmail,
        formData.password,
        formData.confirmPassword,
        formData.companyName,
        formData.salutation + " " + formData.contactName,
        formData.phoneNumber,
        formData.website
      );
      setRegisteredEmail(formData.email);
      setEmailSent(response.emailSent !== false); // Default to true if not specified
      setRegistrationSuccess(true);
      setError("");
    } catch (err) {
      setError(err.message || "Registration failed");
      setRegistrationSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  if (registrationSuccess) {
    return (
      <main className="auth-page">
        <FormContainer>
          <header>
            <h1 className="auth-title">Registration successful!</h1>
          </header>

          <div className="register-success-container">
            <div className="register-success-icon">✉️</div>
            {emailSent ? (
              <>
                <p className="register-success-text-primary">
                  We have sent an email to <strong>{registeredEmail}</strong>.
                </p>
                <p className="register-success-text-secondary">
                  Please check your inbox and click on the verification link to activate your account.
                </p>
              </>
            ) : (
              <>
                <p className="register-success-text-primary">
                  This email address is already registered but not yet verified.
                </p>
                <p className="register-success-text-secondary">
                  The email could not be sent at the moment. Please try again later or contact support.
                </p>
              </>
            )}
            <Button
              className="auth-button"
              onClick={() => {
                setRegistrationSuccess(false);
                navigate("/login");
              }}
            >
              To Login
            </Button>
          </div>

          <footer className="auth-footer">
            Didn't receive an email?{" "}
            <a
              href="/register"
              onClick={(e) => {
                e.preventDefault();
                setRegistrationSuccess(false);
              }}
            >
              Register again
            </a>
          </footer>
        </FormContainer>
      </main>
    );
  }

  return (
    <main className="auth-page">
      <FormContainer>
        <header>
          <h1 className="auth-title">Register</h1>
          <p className="auth-subtitle">Create your account</p>
        </header>

        <form className="auth-form" onSubmit={handleSubmit}>
          <InputField
            id="email"
            type="email"
            label="E-Mail"
            value={formData.email}
            onChange={handleChange("email")}
            required
            labelContent={
              <>
                Email <span className="required-star">*</span>
                <span className="email-info">
                  ⓘ
                  <span className="tooltip">
                  Login only possible with company domain.
                </span>
                </span>
              </>
            }
          />

          <InputField
            id="confirmEmail"
            type="email"
            label="Confirm Email"
            value={formData.confirmEmail}
            onChange={handleChange("confirmEmail")}
            required
          />

          <InputField
            id="password"
            type={showPassword ? "text" : "password"}
            label="Password"
            value={formData.password}
            onChange={handleChange("password")}
            required
            inputWrapperClassName="auth-input-wrapper auth-input-wrapper--has-toggle"
            rightElement={
              <button
                type="button"
                className="password-toggle-button"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label="Toggle password visibility"
                aria-pressed={showPassword}
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <VisibilityOffIcon aria-hidden="true" />
                ) : (
                  <VisibilityIcon aria-hidden="true" />
                )}
              </button>
            }
          />

          <PasswordRules password={formData.password} />

          <InputField
            id="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            label="Confirm Password"
            value={formData.confirmPassword}
            onChange={handleChange("confirmPassword")}
            required
            inputWrapperClassName="auth-input-wrapper auth-input-wrapper--has-toggle"
            rightElement={
              <button
                type="button"
                className="password-toggle-button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                aria-label="Toggle confirm password visibility"
                aria-pressed={showConfirmPassword}
                title={
                  showConfirmPassword
                    ? "Hide confirm password"
                    : "Show confirm password"
                }
              >
                {showConfirmPassword ? (
                  <VisibilityOffIcon aria-hidden="true" />
                ) : (
                  <VisibilityIcon aria-hidden="true" />
                )}
              </button>
            }
          />

          <h4>Contact Person</h4>

          <SelectField
            id="salutation"
            label="Salutation"
            value={formData.salutation}
            onChange={handleChange("salutation")}
            required
            options={[
              { value: "", label: "Please select" },
              { value: "Herr", label: "Mr." },
              { value: "Frau", label: "Ms." },
              { value: "Divers", label: "Diverse" },
              { value: "Keine Angabe", label: "Not specified" },
            ]}
          />

          <InputField
            id="contactName"
            label="First and Last Name"
            value={formData.contactName}
            onChange={handleChange("contactName")}
            required
          />

          <InputField
            id="phoneNumber"
            label="Phone Number"
            value={formData.phoneNumber}
            onChange={handleChange("phoneNumber")}
            required
          />

          <h4>Company</h4>

          <InputField
            id="companyName"
            label="Company Name"
            value={formData.companyName}
            onChange={handleChange("companyName")}
            required
          />

          <InputField
            id="website"
            label="Website (optional)"
            value={formData.website}
            onChange={handleChange("website")}
          />

          <FormError message={error} />

          <Button
            type="submit"
            className="auth-button"
            disabled={loading}
            loading={loading}
            loadingText="Sending…"
          >
            Register
          </Button>
        </form>

        <footer className="auth-footer">
          Already have an account? <Link to="/login"> Log in now </Link>
        </footer>
      </FormContainer>
    </main>
  );
};

export default Register;
